import React from 'react';
import { camelCase } from 'lodash';
import { useTranslation } from 'react-i18next';

import { PLATFORM_THEME_COLORS, Typography } from '@thinknet-gmbh/platform-common-ui';
import { displayAddress, generateReactKey } from 'src/common/helpers/common.helpers';
import { dateFormat } from 'src/common/helpers/date.helpers';

import { IWorkingConditionItem } from 'src/components/employee/employee.interfaces';

import { MainDetailsContainer } from './table-details-item.styles';

import {
  InfoBlock,
  InfoBlockContainer,
  InfoElement,
  PersonalInfoMainWrapper,
  StyledContainer,
  TitleRowLabel,
} from '../../../employee-common.styles';

const TableDetailsItem = ({ workingCondition }: { workingCondition: IWorkingConditionItem }) => {
  const { t } = useTranslation(['common', 'employee', 'settings']);

  const {
    active,
    employmentType,
    contracts,
    company,
    companyDepartment,
    companyBusinessAddress,
    companyLocation,
    jobSupervisor,
    mainOrSecondaryOccupation,
    occupationType,
    paidVacation,
    weeklyHours,
    terminatedBy,
    terminationDate,
    noticePeriod,
    noticePeriodType,
    referencePeriod,
    contractExpirationDate,
  } = workingCondition;

  return (
    <MainDetailsContainer>
      <PersonalInfoMainWrapper>
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:workingConditions.labels.generalInformation')}
          </TitleRowLabel>
          <InfoBlock columns={'1.5fr 1fr 1fr 1fr'}>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t(
                  'employee:labels.activeWorkingCondition'
                )}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {t(`common:labels.${active ? 'yes' : 'no'}`)}
                </Typography>
              </InfoElement>
              {contracts?.map(el => {
                return (
                  <InfoElement key={generateReactKey()}>
                    <Typography weight="bold">{`${t(
                      'employee:workingConditions.labels.contractType.title'
                    )}:`}</Typography>
                    <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                      {el?.contractType
                        ? t(
                            `employee:workingConditions.labels.contractType.${camelCase(
                              el.contractType
                            )}`
                          )
                        : '-'}
                    </Typography>
                  </InfoElement>
                );
              })}
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t('employee:labels.employmentType')}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {t(`employee:workingConditions.labels.${employmentType?.toLowerCase()}`)}
                </Typography>
              </InfoElement>
              {contracts?.map(el => {
                return (
                  <InfoElement key={generateReactKey()}>
                    <Typography weight="bold">{`${t('common:labels.hireDate')}:`}</Typography>
                    <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                      {el.hireDate ? dateFormat(el.hireDate) : '-'}
                    </Typography>
                  </InfoElement>
                );
              })}
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t(
                  'employee:labels.lengthOfProbation'
                )}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {workingCondition?.lengthOfProbation
                    ? `${workingCondition.lengthOfProbation} ${t(
                        `employee:workingConditions.labels.probationTypes.${workingCondition?.probationType?.toLowerCase()}`
                      )}`
                    : '-'}
                </Typography>
              </InfoElement>
              {contracts?.map(el => {
                return (
                  <InfoElement key={generateReactKey()}>
                    <Typography weight="bold">{`${t(
                      'employee:workingConditions.labels.endDate'
                    )}:`}</Typography>
                    <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                      {el?.endDate ? dateFormat(el.endDate) : '-'}
                    </Typography>
                  </InfoElement>
                );
              })}
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t('employee:labels.probationDate')}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {workingCondition.probationDate
                    ? dateFormat(workingCondition.probationDate)
                    : '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
          </InfoBlock>
        </StyledContainer>
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:workingConditions.labels.companyAndLocation')}
          </TitleRowLabel>
          <InfoBlock columns={'1.5fr 1fr 1fr 1fr'}>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t(
                  'settings:company.labels.companyName'
                )}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {company?.name || '-'}
                </Typography>
              </InfoElement>
              <InfoElement>
                <Typography weight="bold">{`${t('common:labels.businessAddress')}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {workingCondition.companyBusinessAddress
                    ? displayAddress(companyBusinessAddress)
                    : '-'}
                </Typography>
              </InfoElement>
              <InfoElement>
                <Typography weight="bold">{`${t('employee:labels.costCenter')}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {companyDepartment?.costCenter || '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t('common:labels.location')}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {companyLocation?.name || '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t('common:labels.department')}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {companyDepartment?.name || '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
          </InfoBlock>
        </StyledContainer>
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:workingConditions.labels.position')}
          </TitleRowLabel>
          <InfoBlock columns={'1.5fr 1fr 1fr 1fr'}>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t('common:labels.jobTitle')}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {workingCondition?.job?.title || '-'}
                </Typography>
              </InfoElement>
              <InfoElement>
                <Typography weight="bold">{`${t('employee:labels.supervisor')}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {jobSupervisor?.supervisorJobTitle}
                  {jobSupervisor?.supervisorMember?.profile
                    ? `(${jobSupervisor?.supervisorMember?.profile.displayName})`
                    : ''}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t('employee:labels.occupationType')}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {t(
                    `employee:workingConditions.labels.${camelCase(occupationType.toLowerCase())}`
                  )}
                </Typography>
              </InfoElement>
              <InfoElement>
                <Typography weight="bold">{`${t(
                  'employee:labels.secondaryOccupation'
                )}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {t(
                    `employee:workingConditions.labels.${mainOrSecondaryOccupation.toLowerCase()}`
                  )}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
          </InfoBlock>
        </StyledContainer>
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:workingConditions.labels.workingAndVacation')}
          </TitleRowLabel>
          <InfoBlock columns={'1.5fr 1fr 1fr 1fr'} >
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t('employee:labels.weeklyHours')}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>{`${weeklyHours ||
                  '-'} hr`}</Typography>
              </InfoElement>
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t(
                  'employee:workingConditions.labels.paidVacation'
                )}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>{`${paidVacation ||
                  '-'} ${t('common:labels.times.days')}`}</Typography>
              </InfoElement>
            </InfoBlockContainer>
          </InfoBlock>
        </StyledContainer>
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:workingConditions.labels.cancelInformation')}
          </TitleRowLabel>
          <InfoBlock columns={'1.5fr 1fr 1fr 1fr'}>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t('employee:labels.noticePeriod')}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>{`${noticePeriod ||
                  '-'} ${
                  noticePeriodType
                    ? t(
                        `employee:workingConditions.labels.probationTypes.${noticePeriodType.toLowerCase()}`
                      )
                    : ''
                }`}</Typography>
              </InfoElement>
              <InfoElement>
                <Typography weight="bold">{`${t('employee:labels.terminationDate')}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {terminationDate ? dateFormat(terminationDate) : '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t(
                  'employee:workingConditions.labels.referencePeriod'
                )}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {referencePeriod
                    ? t(
                        `employee:workingConditions.labels.referencePeriodOptions.${referencePeriod.toLowerCase()}`
                      )
                    : '-'}
                </Typography>
              </InfoElement>
              <InfoElement>
                <Typography weight="bold">{`${t(
                  'employee:workingConditions.labels.terminatedBy'
                )}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {terminationDate
                    ? t(
                        `employee:workingConditions.labels.terminatedTypes.${terminatedBy?.toLowerCase()}`
                      )
                    : '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">{`${t('employee:labels.contractEnds')}:`}</Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {contractExpirationDate ? dateFormat(contractExpirationDate) : '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
          </InfoBlock>
        </StyledContainer>
      </PersonalInfoMainWrapper>
    </MainDetailsContainer>
  );
};

export default TableDetailsItem;
